import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyWatchHistory } from "@/lib/user.functions";
import {
  ArrowLeft,
  CheckCircle2,
  PlayCircle,
  Clock,
  BookOpen,
  TrendingUp,
  History,
} from "lucide-react";
import { useMemo } from "react";
import { formatDateBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/perfil/historico")({
  head: () => ({ meta: [{ title: "Meu histórico · Beit Tefilah Rechovot" }] }),
  component: HistoryPage,
});

function fmtDate(iso: string | null) {
  if (!iso) return "";
  try {
    return formatDateBR(iso, { day: "2-digit", month: "2-digit", year: "numeric" }, "");
  } catch {
    return "";
  }
}

function fmtHM(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

function HistoryPage() {
  const fn = useServerFn(getMyWatchHistory);
  const { data, isLoading } = useQuery({ queryKey: ["my-watch-history"], queryFn: () => fn() });

  const stats = useMemo(() => {
    const rows = data ?? [];
    const total = rows.length;
    const completed = rows.filter((r: any) => r._completed).length;
    const totalWatchedSec = rows.reduce(
      (sum: number, r: any) => sum + (Number(r._progress) || 0),
      0,
    );
    const parashaCount: Record<string, number> = {};
    rows.forEach((r: any) => {
      const name = r.parashiot?.name_translit;
      if (name) parashaCount[name] = (parashaCount[name] ?? 0) + 1;
    });
    const topParasha = Object.entries(parashaCount).sort((a, b) => b[1] - a[1])[0];
    const lastWatched = rows[0]?._watchedAt as string | undefined;
    return { total, completed, totalWatchedSec, topParasha, lastWatched };
  }, [data]);

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/perfil"
          className="inline-flex items-center gap-1 text-sm text-foreground/70 hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar ao perfil
        </Link>
        <h1 className="mt-2 flex items-center gap-2 font-serif text-3xl font-semibold text-primary">
          <History className="h-7 w-7 text-gold" /> Meu histórico de ensinos
        </h1>
        <p className="mt-1 text-foreground/75">
          Um panorama completo do que você já assistiu na Beit Tefilah Rechovot.
        </p>
      </div>

      {isLoading ? (
        <div
          className="min-h-[60vh] animate-pulse rounded-xl bg-secondary"
          role="status"
          aria-label="Carregando"
        />
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={<BookOpen className="h-5 w-5" />}
              label="Ensinos iniciados"
              value={String(stats.total)}
            />
            <StatCard
              icon={<CheckCircle2 className="h-5 w-5" />}
              label="Concluídos"
              value={String(stats.completed)}
              sub={
                stats.total > 0
                  ? `${Math.round((stats.completed / stats.total) * 100)}% do total`
                  : undefined
              }
            />
            <StatCard
              icon={<Clock className="h-5 w-5" />}
              label="Tempo assistido"
              value={fmtHM(stats.totalWatchedSec)}
            />
            <StatCard
              icon={<TrendingUp className="h-5 w-5" />}
              label="Parashá mais estudada"
              value={stats.topParasha?.[0] ?? "—"}
              sub={stats.topParasha ? `${stats.topParasha[1]} ensino(s)` : undefined}
            />
          </section>

          <section>
            <h2 className="mb-3 font-serif text-xl font-semibold text-primary">
              Todos os ensinos assistidos
            </h2>
            {(data?.length ?? 0) === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-12 text-center">
                <p className="text-foreground/75">Você ainda não assistiu a nenhum ensino.</p>
                <Link
                  to="/ensinos"
                  className="mt-4 inline-block font-medium text-primary hover:underline"
                >
                  Explorar catálogo →
                </Link>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="hidden grid-cols-[1fr_160px_140px_120px] gap-4 border-b border-border bg-secondary/40 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground/70 sm:grid">
                  <div>Ensino</div>
                  <div>Parashá</div>
                  <div>Assistido em</div>
                  <div className="text-right">Progresso</div>
                </div>
                <ul>
                  {data!.map((t: any) => {
                    const pct =
                      t._duration && t._progress
                        ? Math.min(100, Math.round((t._progress / t._duration) * 100))
                        : null;
                    return (
                      <li key={t.id} className="border-b border-border last:border-b-0">
                        <Link
                          to="/ensinos/$id"
                          params={{ id: t.id }}
                          className="grid gap-2 px-4 py-4 transition-colors hover:bg-secondary/40 sm:grid-cols-[1fr_160px_140px_120px] sm:items-center"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              {t._completed ? (
                                <CheckCircle2
                                  className="h-4 w-4 shrink-0 text-primary"
                                  aria-label="Concluído"
                                />
                              ) : (
                                <PlayCircle
                                  className="h-4 w-4 shrink-0 text-gold"
                                  aria-label="Em progresso"
                                />
                              )}
                              <span className="truncate font-medium text-foreground">
                                {t.title}
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-foreground/70 sm:hidden">
                              {t.parashiot?.name_translit ?? "—"} · {fmtDate(t._watchedAt)}
                            </div>
                            {pct !== null && !t._completed && (
                              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-secondary sm:hidden">
                                <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                              </div>
                            )}
                          </div>
                          <div className="hidden text-sm text-foreground/80 sm:block">
                            {t.parashiot?.name_translit ?? "—"}
                          </div>
                          <div className="hidden text-sm text-foreground/80 sm:block">
                            {fmtDate(t._watchedAt)}
                          </div>
                          <div className="hidden text-right text-sm sm:block">
                            {t._completed ? (
                              <span className="font-semibold text-primary">Concluído</span>
                            ) : pct !== null ? (
                              <div className="flex items-center justify-end gap-2">
                                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
                                  <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-foreground/80">{pct}%</span>
                              </div>
                            ) : (
                              <span className="text-foreground/70">{fmtHM(t._progress || 0)}</span>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-primary">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
          {icon}
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
          {label}
        </span>
      </div>
      <p className="mt-3 font-serif text-2xl font-semibold text-primary">{value}</p>
      {sub && <p className="mt-1 text-xs text-foreground/70">{sub}</p>}
    </div>
  );
}
